import { create } from 'zustand';
import { bimKernel } from '../core/BIMKernel';
import { useDocumentStore } from './useDocumentStore';
import { bimElementToSceneObject, type SceneObject } from '../core/scene';
import { Project, BIMElement, ToolType, ToolProperties, View, Level, Grid, Sheet, PlacedView, Schedule } from '../core/types';

export interface BIMState {
    project: Project;
    selection: string[];
    activeTool: ToolType;
    toolProperties: ToolProperties;

    // Actions - Elements
    addElement: (element: BIMElement) => void;
    removeElement: (id: string) => void;
    updateElement: (id: string, updates: Partial<BIMElement>) => void;
    updateElements: (batch: Array<{ id: string; updates: Partial<BIMElement> }>) => void;
    /** Atomic remove + add as one undoable command (Precast split/reinforcement). */
    replaceElements: (description: string, removeIds: string[], addElements: BIMElement[]) => void;

    // Actions - Views
    setActiveView: (viewId: string) => void;
    updateView: (viewId: string, updates: Partial<View>) => void;
    createView: (name: string, type: View['type']) => View;
    addView: (view: View) => void;
    removeView: (viewId: string) => void;

    // Actions - Schedules
    createSchedule: (schedule: Schedule) => void;
    removeSchedule: (scheduleId: string) => void;

    // Actions - Sheets
    createSheet: (number: string, name: string, titleBlockId: string, style?: string) => void;
    removeSheet: (sheetId: string) => void;
    updateSheet: (sheetId: string, updates: Partial<Sheet>) => void;
    setActiveSheet: (sheetId: string | null) => void;
    placeViewOnSheet: (sheetId: string, viewId: string, position: { x: number; y: number }, scale?: number) => void;
    removePlacedView: (sheetId: string, placedViewId: string) => void;
    movePlacedView: (sheetId: string, placedViewId: string, position: { x: number; y: number }) => void;
    createCustomTitleBlock: (name: string, widthMm: number, heightMm: number) => void;

    // Actions - Levels & Grids
    addLevel: (name: string, elevation: number) => void;
    updateLevel: (id: string, updates: Partial<Level>) => void;
    addGrid: (name: string, start: [number, number], end: [number, number]) => void;
    updateGrid: (id: string, updates: Partial<Grid>) => void;

    // Actions - Project Info (Manage > Location)
    updateProjectInfo: (updates: Partial<Pick<Project, 'name' | 'number' | 'client' | 'address'>>) => void;

    // Actions - Phasing (Manage > Phasing)
    addPhase: (name: string) => void;
    renamePhase: (id: string, name: string) => void;
    removePhase: (id: string) => void;
    setActivePhase: (id: string) => void;
    assignPhaseToElements: (elementIds: string[], phaseId: string) => void;

    // Actions - Design Options (Manage > Design Options)
    addDesignOptionSet: (name: string) => void;
    addDesignOption: (setId: string, name: string) => void;
    removeDesignOption: (setId: string, optionId: string) => void;
    setActiveDesignOption: (setId: string, optionId: string) => void;
    assignDesignOptionToElements: (elementIds: string[], optionId: string | null) => void;

    // Actions - Materials (Manage > Materials)
    createMaterial: (name: string) => string;
    updateMaterial: (
        id: string,
        patch: {
            name?: string;
            identity?: Partial<import('../core/MaterialTypes').MaterialIdentity>;
            graphics?: Partial<import('../core/MaterialTypes').MaterialGraphics>;
            physical?: Partial<import('../core/MaterialTypes').MaterialPhysical>;
            thermal?: Partial<import('../core/MaterialTypes').MaterialThermal>;
        },
    ) => void;
    duplicateMaterial: (id: string, duplicateAsset: boolean) => string | null;
    duplicateAppearanceAsset: (materialId: string) => void;
    updateAppearanceAsset: (assetId: string, patch: Partial<import('../core/types').AppearanceAsset>) => void;
    deleteMaterials: (ids: string[]) => void;
    purgeUnusedMaterials: () => number;
    assignMaterialToElements: (elementIds: string[], materialId: string) => void;
    getMaterialUsage: (material: import('../core/types').BIMMaterial) => string[];
    importMaterials: (payload: {
        materials?: import('../core/types').BIMMaterial[];
        assets?: import('../core/types').AppearanceAsset[];
    }) => number;
    exportMaterials: () => {
        materials: import('../core/types').BIMMaterial[];
        assets: import('../core/types').AppearanceAsset[];
    };

    // Actions - Selection & Tools
    setSelection: (ids: string[]) => void;
    addToSelection: (id: string) => void;
    clearSelection: () => void;
    setActiveTool: (tool: ToolType) => void;
    /** App-store → BIM-store tool mirror (no selection clear; see bridge). */
    mirrorActiveTool: (tool: ToolType) => void;
    setToolProperty: <K extends keyof ToolProperties>(key: K, value: ToolProperties[K]) => void;

    // Actions - Undo/Redo
    undo: () => void;
    redo: () => void;
    canUndo: () => boolean;
    canRedo: () => boolean;

    // Actions - Persistence
    saveProject: () => Promise<string>;
    loadProject: (json: string) => Promise<void>;
    /** Object entry of loadProject — already-parsed payloads skip the extra
        JSON round trip on document load / session tab restore. */
    loadProjectData: (data: Record<string, unknown>) => Promise<void>;

    // Internal
    _refresh: () => void;
}

/**
 * Mirror every BIM element into the document scene ledger (Phase A2).
 * The kernel stays the owner of the rich model; the ledger is the
 * protocol-level view that other workbenches (CAD command line, shared
 * viewport overlays, persistence of cross-mode content) consume.
 * Called from _refresh, i.e. once per kernel mutation batch.
 *
 * Kernel element mutations are replace-on-write (untouched elements keep
 * their object identity), so the element reference is a free change token:
 * a command that touches K elements re-projects only those K instead of
 * the whole model, and the ledger record is rewritten only when the
 * projection actually changed. Reuse is safe because
 * bimElementToSceneObject is a pure function of the element.
 */
const projectionCache = new Map<string, { ref: BIMElement; obj: SceneObject }>();

function syncBimLedger(): void {
    const elements = bimKernel.getProject().elements;
    let dirty = elements.size !== projectionCache.size;
    const objects: SceneObject[] = [];
    elements.forEach((element, id) => {
        const cached = projectionCache.get(id);
        if (cached && cached.ref === element) {
            objects.push(cached.obj);
            return;
        }
        const obj = bimElementToSceneObject(element);
        projectionCache.set(id, { ref: element, obj });
        objects.push(obj);
        dirty = true;
    });
    if (!dirty) return;
    // Drop projections of elements the kernel no longer has.
    for (const id of [...projectionCache.keys()]) {
        if (!elements.has(id)) projectionCache.delete(id);
    }
    useDocumentStore.getState().replaceSceneLedgerSource('bim', objects);
}

export const useBIMStore = create<BIMState>((set, get) => ({
    project: bimKernel.getProject(),
    selection: [],
    activeTool: 'SELECT',
    toolProperties: {
        wallHeight: 3000,
        wallBaseOffset: 0,
        wallLocationLine: 'WallCenterline',
        wallChain: true,
        wallJoinType: 'Butt',
        tagOnPlacement: true,
    },

    // Refresh state from kernel
    _refresh: () => {
        // Every kernel mutation flows through here, making it the single
        // point where the open document is marked as modified.
        useDocumentStore.getState().markDirty();
        set({ project: { ...bimKernel.getProject() } });
        syncBimLedger();
    },

    addElement: (element: BIMElement) => {
        bimKernel.addElement(element);
        get()._refresh();
    },

    removeElement: (id: string) => {
        bimKernel.removeElement(id);
        get()._refresh();
    },

    updateElement: (id: string, updates: Partial<BIMElement>) => {
        bimKernel.updateElement(id, updates);
        get()._refresh();
    },

    updateElements: (batch: Array<{ id: string; updates: Partial<BIMElement> }>) => {
        bimKernel.updateElements(batch);
        get()._refresh();
    },

    replaceElements: (description: string, removeIds: string[], addElements: BIMElement[]) => {
        bimKernel.replaceElements(description, removeIds, addElements);
        get()._refresh();
    },

    setActiveView: (viewId: string) => {
        bimKernel.setActiveView(viewId);
        get()._refresh();
    },

    updateView: (viewId: string, updates: Partial<View>) => {
        bimKernel.updateView(viewId, updates);
        get()._refresh();
    },

    createView: (name: string, type: View['type']) => {
        const view = bimKernel.createView(name, type);
        get()._refresh();
        return view;
    },

    addView: (view: View) => {
        // Replace-on-write in the kernel: fresh map identity so the Project
        // Browser and viewport observe the new view immediately.
        bimKernel.addView(view);
        get()._refresh();
    },

    removeView: (viewId: string) => {
        bimKernel.removeView(viewId);
        get()._refresh();
    },

    // --- Schedules ---
    createSchedule: (schedule: Schedule) => {
        bimKernel.createSchedule(schedule);
        get()._refresh();
    },

    removeSchedule: (scheduleId: string) => {
        bimKernel.removeSchedule(scheduleId);
        get()._refresh();
    },

    // --- Sheets ---
    createSheet: (number: string, name: string, titleBlockId: string, style?: string) => {
        bimKernel.createSheet(number, name, titleBlockId, style);
        get()._refresh();
    },

    removeSheet: (sheetId: string) => {
        bimKernel.removeSheet(sheetId);
        get()._refresh();
    },

    updateSheet: (sheetId: string, updates: Partial<Sheet>) => {
        bimKernel.updateSheet(sheetId, updates);
        get()._refresh();
    },

    setActiveSheet: (sheetId: string | null) => {
        bimKernel.setActiveSheet(sheetId);
        get()._refresh();
    },

    placeViewOnSheet: (sheetId: string, viewId: string, position: { x: number; y: number }, scale?: number) => {
        const sheet = bimKernel.getProject().sheets.get(sheetId);
        if (!sheet) return;
        const view = bimKernel.getProject().views.get(viewId);
        const placedView: PlacedView = {
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
            viewId,
            position,
            viewportType: 'Viewport',
            scale: scale ?? view?.scale
        };
        bimKernel.placeViewOnSheet(sheetId, placedView);
        get()._refresh();
    },

    removePlacedView: (sheetId: string, placedViewId: string) => {
        bimKernel.removePlacedView(sheetId, placedViewId);
        get()._refresh();
    },

    movePlacedView: (sheetId: string, placedViewId: string, position: { x: number; y: number }) => {
        bimKernel.movePlacedView(sheetId, placedViewId, position);
        get()._refresh();
    },

    createCustomTitleBlock: (name: string, widthMm: number, heightMm: number) => {
        bimKernel.createCustomTitleBlock(name, widthMm, heightMm);
        get()._refresh();
    },

    addLevel: (name: string, elevation: number) => {
        bimKernel.addLevel(name, elevation);
        get()._refresh();
    },

    addGrid: (name: string, start: [number, number], end: [number, number]) => {
        bimKernel.addGrid(name, start, end);
        get()._refresh();
    },

    updateGrid: (id: string, updates: Partial<Grid>) => {
        bimKernel.updateGrid(id, updates);
        get()._refresh();
    },

    // --- Project Info ---
    updateProjectInfo: (updates) => {
        bimKernel.updateProjectInfo(updates);
        get()._refresh();
    },

    // --- Phasing ---
    addPhase: (name) => {
        bimKernel.addPhase(name);
        get()._refresh();
    },

    renamePhase: (id, name) => {
        bimKernel.renamePhase(id, name);
        get()._refresh();
    },

    removePhase: (id) => {
        bimKernel.removePhase(id);
        get()._refresh();
    },

    setActivePhase: (id) => {
        bimKernel.setActivePhase(id);
        get()._refresh();
    },

    assignPhaseToElements: (elementIds, phaseId) => {
        bimKernel.assignPhaseToElements(elementIds, phaseId);
        get()._refresh();
    },

    // --- Design Options ---
    addDesignOptionSet: (name) => {
        bimKernel.addDesignOptionSet(name);
        get()._refresh();
    },

    addDesignOption: (setId, name) => {
        bimKernel.addDesignOption(setId, name);
        get()._refresh();
    },

    removeDesignOption: (setId, optionId) => {
        bimKernel.removeDesignOption(setId, optionId);
        get()._refresh();
    },

    setActiveDesignOption: (setId, optionId) => {
        bimKernel.setActiveDesignOption(setId, optionId);
        get()._refresh();
    },

    assignDesignOptionToElements: (elementIds, optionId) => {
        bimKernel.assignDesignOptionToElements(elementIds, optionId);
        get()._refresh();
    },

    // --- Materials ---
    createMaterial: (name) => {
        const id = bimKernel.createMaterial(name);
        get()._refresh();
        return id;
    },

    updateMaterial: (id, patch) => {
        bimKernel.updateMaterial(id, patch);
        get()._refresh();
    },

    duplicateMaterial: (id, duplicateAsset) => {
        const newId = bimKernel.duplicateMaterial(id, duplicateAsset);
        get()._refresh();
        return newId;
    },

    duplicateAppearanceAsset: (materialId) => {
        bimKernel.duplicateAppearanceAsset(materialId);
        get()._refresh();
    },

    updateAppearanceAsset: (assetId, patch) => {
        bimKernel.updateAppearanceAsset(assetId, patch);
        get()._refresh();
    },

    deleteMaterials: (ids) => {
        bimKernel.deleteMaterials(ids);
        get()._refresh();
    },

    purgeUnusedMaterials: () => {
        const count = bimKernel.purgeUnusedMaterials();
        if (count > 0) get()._refresh();
        return count;
    },

    assignMaterialToElements: (elementIds, materialId) => {
        bimKernel.assignMaterialToElements(elementIds, materialId);
        get()._refresh();
    },

    getMaterialUsage: (material) => bimKernel.getMaterialUsage(material),

    importMaterials: (payload) => {
        const count = bimKernel.importMaterials(payload);
        if (count > 0) get()._refresh();
        return count;
    },

    exportMaterials: () => bimKernel.exportMaterials(),

    updateLevel: (id: string, updates: Partial<Level>) => {
        bimKernel.updateLevel(id, updates);
        get()._refresh();
    },

    setSelection: (ids: string[]) => {
        set({ selection: ids });
    },

    addToSelection: (id: string) => {
        const current = get().selection;
        if (!current.includes(id)) {
            set({ selection: [...current, id] });
        }
    },

    clearSelection: () => {
        set({ selection: [] });
    },

    setActiveTool: (tool: ToolType) => {
        set({ activeTool: tool, selection: [] });
    },

    /**
     * Mirror-sync entry from the app store (toolSyncBridge). Updates only the
     * tool id — never the selection — so the store-to-store echo can never
     * wipe a live selection the moment the user activates a tool.
     */
    mirrorActiveTool: (tool: ToolType) => {
        set({ activeTool: tool });
    },

    setToolProperty: <K extends keyof ToolProperties>(key: K, value: ToolProperties[K]) => {
        set(state => ({
            toolProperties: { ...state.toolProperties, [key]: value }
        }));
    },

    undo: () => {
        bimKernel.undo();
        get()._refresh();
    },

    redo: () => {
        bimKernel.redo();
        get()._refresh();
    },

    canUndo: () => bimKernel.canUndo(),
    canRedo: () => bimKernel.canRedo(),

    saveProject: async () => {
        return bimKernel.saveProject();
    },

    loadProject: async (json: string) => {
        await bimKernel.loadProject(json);
        get()._refresh();
    },

    loadProjectData: async (data: Record<string, unknown>) => {
        bimKernel.loadProjectData(data);
        get()._refresh();
    }
}));

// Seed the ledger with the kernel's initial (default template) content so
// shared consumers see BIM elements before the first mutation happens.
syncBimLedger();
